/* global process */

import vClickOutside from 'click-outside-vue3'
import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import Icon from 'src/components/icon/icon.js'
import IconLayers from 'src/components/icon/icon_layers.js'
import InstancePicker from 'src/components/instance_picker/instance_picker.vue'
import RichContent from 'src/components/rich_content/rich_content.jsx'
import Status from 'src/components/status/status.vue'
import StillImage from 'src/components/still-image/still-image.vue'
import App from '../App.vue'
import { storage } from '../lib/storage.js'
import FaviconService from '../services/favicon_service/favicon_service.js'
import { applyLayoutVariables } from '../services/layout_vars/layout_vars.js'
import { initServiceWorker, updateFocus } from '../services/sw/sw.js'
import { startUpdateCheck } from '../services/update_check/update_check.js'
import {
  windowHeight,
  windowWidth,
} from '../services/window_utils/window_utils'
import {
  applyInstanceState,
  instanceToState,
  nodeInfoToState,
} from './instance_data.js'
import routes from './routes'

import { useAuthFlowStore } from 'src/stores/auth_flow'
import { useChatsStore } from 'src/stores/chats.js'
import { useEmojiStore } from 'src/stores/emoji.js'
import { useI18nStore } from 'src/stores/i18n'
import { useInstanceStore } from 'src/stores/instance.js'
import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'
import { useInterfaceStore } from 'src/stores/interface.js'
import { useLocalConfigStore } from 'src/stores/local_config.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useNotificationsStore } from 'src/stores/notifications.js'
import { useOAuthStore } from 'src/stores/oauth.js'
import { useStatusesStore } from 'src/stores/statuses.js'
import { useSyncConfigStore } from 'src/stores/sync_config.js'
import { useUserHighlightStore } from 'src/stores/user_highlight.js'
import { useUsersStore } from 'src/stores/users.js'

import { apiUrl, setApiBase } from 'src/api/api_base.js'
import VBodyScrollLock from 'src/directives/body_scroll_lock'
import {
  INSTANCE_IDENTITY_DEFAULT_DEFINITIONS,
  INSTANCE_IDENTIY_EXTERNAL,
  settingDefaults,
} from 'src/modules/default_config_state.js'
import {
  chosenInstance,
  instanceConfig,
  isHosted,
} from 'src/services/hosted/hosted.js'

// --- fetching

let staticInitialResults = null

// the answers the server put into index.html, if any
const parsedInitialResults = () => {
  const element = document.getElementById('initial-results')
  if (!element) return null
  staticInitialResults ??= JSON.parse(element.textContent)
  return staticInitialResults
}

const decodeUTF8Base64 = (data) => {
  const rawData = atob(data)
  const array = Uint8Array.from([...rawData].map((char) => char.codePointAt(0)))
  return new TextDecoder().decode(array)
}

// a response from index.html's preloaded answers, else from the server
const preloadFetch = async (request) => {
  const data = parsedInitialResults()
  if (!data?.[request]) {
    return window.fetch(apiUrl(request))
  }
  const requestData = JSON.parse(decodeUTF8Base64(data[request]))
  return {
    ok: true,
    json: () => requestData,
    text: () => requestData,
  }
}

// A file the boot needs: its JSON (or text), or undefined when it cannot
// be had; what failed is logged with `what`. preload: try index.html's
// answers first. The app starts without any of these.
const bootFetch = async (
  path,
  { what, preload = false, as = 'json', level = 'warn' },
) => {
  try {
    const res = await (preload ? preloadFetch(path) : window.fetch(path))
    if (!res.ok) throw res
    return await res[as]()
  } catch (error) {
    console[level](`Could not load ${what}`, error)
  }
}

// --- instance data

const getInstanceConfig = async () => {
  const data = await bootFetch('/api/v1/instance', {
    what: 'instance config, potentially fatal',
    preload: true,
    level: 'error',
  })
  if (data) applyInstanceState(instanceToState(data))
  // scrobbles support needs a user id: checked where they are fetched
}

const getNodeInfo = async () => {
  const data =
    (await bootFetch('/nodeinfo/2.1.json', {
      what: 'nodeinfo 2.1, trying 2.0',
      preload: true,
      level: 'debug',
    })) ??
    (await bootFetch('/nodeinfo/2.0.json', { what: 'nodeinfo', preload: true }))
  if (!data) return
  try {
    applyInstanceState(nodeInfoToState(data))
  } catch (e) {
    console.warn('Could not read nodeinfo', e)
  }
}

const getInstancePanel = async () => {
  const html = await bootFetch('/instance/panel.html', {
    what: 'instance panel',
    preload: true,
    as: 'text',
  })
  if (html !== undefined) {
    useInstanceStore().set({
      path: 'instanceIdentity.instanceSpecificPanelContent',
      value: html,
    })
  }
}

const getTOS = async () => {
  const html = await bootFetch(apiUrl('/static/terms-of-service.html'), {
    what: 'TOS',
    as: 'text',
  })
  if (html !== undefined) {
    useInstanceStore().set({ path: 'instanceIdentity.tos', value: html })
  }
}

// the instance's sticker packs (hosted: on the instance)
const getStickers = async () => {
  const values = await bootFetch(apiUrl('/static/stickers.json'), {
    what: 'stickers',
  })
  if (!values) return
  const stickers = await Promise.all(
    Object.entries(values).map(async ([name, packPath]) => {
      const path = apiUrl(packPath)
      const meta =
        (await bootFetch(path + 'pack.json', {
          what: `sticker pack ${name}`,
        })) ?? {}
      return { pack: name, path, meta }
    }),
  )
  // a pack without its pack.json sorts by its name
  const title = (sticker) => sticker.meta.title ?? sticker.pack
  useEmojiStore().setStickers(
    stickers.sort((a, b) => title(a).localeCompare(title(b))),
  )
}

// whether the server can translate posts; only /api/v2/instance says so,
// and the app works the same without it
const getTranslationSupport = async () => {
  const data = await bootFetch(apiUrl('/api/v2/instance'), {
    what: 'translation support',
  })
  if (!data) return
  useInstanceCapabilitiesStore().set(
    'translationAvailable',
    !!data.configuration?.translation?.enabled,
  )
}

// --- frontend config

const getStaticConfig = async () =>
  (await bootFetch('/static/config.json', {
    what: 'static/config.json, continuing without it',
  })) ?? {}

// static/config.json, then what the instance's admin set (backend wins,
// unless a developer asks for the static file to)
const applyConfig = async (staticConfig) => {
  const backendConfig = await bootFetch(
    apiUrl('/api/pleroma/frontend_configurations'),
    { what: 'backend-provided frontend config', level: 'error' },
  ).then((data) => data?.pleroma_fe)
  // hosted: the instance's paths point at the instance, login redirects
  const apiConfig = isHosted(staticConfig)
    ? instanceConfig(backendConfig, apiUrl)
    : backendConfig

  const overrides = window.___pleromafe_dev_overrides || {}
  const staticFirst =
    overrides.staticConfigPreference &&
    window.___pleromafe_mode.NODE_ENV === 'development'
  if (staticFirst) console.warn('OVERRIDING API CONFIG WITH STATIC CONFIG')
  const config = staticFirst
    ? { ...apiConfig, ...staticConfig }
    : { ...staticConfig, ...apiConfig }

  Object.keys(INSTANCE_IDENTITY_DEFAULT_DEFINITIONS).forEach((source) => {
    if (source === 'name') return
    if (INSTANCE_IDENTIY_EXTERNAL.has(source)) return
    useInstanceStore().set({
      value:
        config[source] ?? INSTANCE_IDENTITY_DEFAULT_DEFINITIONS[source].default,
      path: `instanceIdentity.${source}`,
    })
  })

  settingDefaults(config).forEach(([key, value]) =>
    useInstanceStore().set({ value, path: `prefsStorage.${key}` }),
  )

  useAuthFlowStore().setInitialStrategy(config.loginMethod)
}

// --- phases

// every store with its persisted state loaded
const loadStores = async () => {
  // the stores that do not persist technically do not need to be awaited here,
  // but that involves either hard-coding the stores in some place (prone to errors)
  // or writing another vite plugin to analyze which stores needs persisting (++load time)
  const allStores = import.meta.glob('../stores/*.js', { eager: true })
  if (process.env.NODE_ENV === 'development') {
    // do some checks to avoid common errors
    if (!Object.keys(allStores).length) {
      throw new TypeError(
        'No stores are available. Check the code in src/boot/after_store.js',
      )
    }
  }
  await Promise.all(
    Object.entries(allStores).map(async ([name, mod]) => {
      const isStoreName = (name) => name.startsWith('use')
      if (process.env.NODE_ENV === 'development') {
        if (Object.keys(mod).filter(isStoreName).length !== 1) {
          throw new TypeError(
            'Each store file must export exactly one store as a named export. Check your code in src/stores/',
          )
        }
      }
      const storeFuncName = Object.keys(mod).find(isStoreName)
      if (storeFuncName && typeof mod[storeFuncName] === 'function') {
        const p = mod[storeFuncName]().$persistLoaded
        if (!(p instanceof Promise)) {
          throw new TypeError(
            `${name} store's $persistLoaded is not a Promise. The persist plugin is not applied.`,
          )
        }
        await p
      } else {
        throw new TypeError(
          `Store module ${name} does not export a 'use...' function`,
        )
      }
    }),
  )
}

// Hosted mode with no instance chosen yet: ask for one instead of starting
// the app. The theme is applied first so the question looks like the app.
const showInstancePicker = async ({ pinia, i18n }) => {
  try {
    await useInterfaceStore().applyTheme()
  } catch (e) {
    console.warn('No theme for the instance picker', e)
  }
  const picker = createApp(InstancePicker)
  picker.use(pinia)
  picker.use(i18n)
  picker.component('FAIcon', Icon)
  picker.mount('#app')
}

// which server this is the frontend of: the page's own, the chosen one
// when hosted, or a developer's target
const resolveServer = (staticConfig) => {
  const hosted = isHosted(staticConfig)
  const chosen = hosted ? chosenInstance() : null
  if (hosted) setApiBase(chosen)
  useInstanceStore().set({ path: 'hosted', value: hosted })

  const overrides = window.___pleromafe_dev_overrides || {}
  const server = hosted ? chosen : (overrides.target ?? window.location.origin)
  useInstanceStore().set({ path: 'server', value: server })
}

const applyTheme = async () => {
  useInterfaceStore().watchSystemTheme()
  try {
    await useInterfaceStore().applyTheme()
  } catch (e) {
    window.bootError(e)
    throw e
  }
  // The old theme engine kept its compiled CSS here. Nothing reads it any
  // more; this only frees the space in browsers that still have it.
  storage.removeItem('pleromafe-theme-cache').catch(() => undefined)
  localStorage.removeItem('pleroma-fe-theme-cache')

  applyLayoutVariables(useMergedConfigStore().mergedConfig)
}

// the server's data and the login; most of it comes preloaded in
// index.html, so this rarely waits
const loadInstanceData = async () => {
  const oauth = useOAuthStore()
  await Promise.all([
    oauth.userToken && useUsersStore().restoreLogin(oauth.userToken),
    getInstancePanel(),
    getNodeInfo(),
    getInstanceConfig(),
  ])
  // not needed to start
  getTOS()
  getStickers()
  getTranslationSupport()
}

const mountApp = (app, i18n) => {
  const router = createRouter({
    history: createWebHistory(),
    routes: routes(),
    scrollBehavior: (to, _from, savedPosition) => {
      if (to.matched.some((m) => m.meta.dontScroll)) {
        return {}
      }
      return savedPosition || { left: 0, top: 0 }
    },
  })

  // Global WS handlers
  useChatsStore().attachSocket()
  useStatusesStore().attachSocket()

  app.use(router)
  app.use(i18n)

  app.use(vClickOutside)
  app.use(VBodyScrollLock)

  // Phosphor icons, under the tag names the templates use
  app.component('FAIcon', Icon)
  app.component('FALayers', IconLayers)
  app.component('Status', Status)
  app.component('RichContent', RichContent)
  app.component('StillImage', StillImage)

  // remove after vue 3.3
  app.config.unwrapInjectedRef = true

  app.mount('#app')

  // a new deploy: offer a reload instead of running the old code for days
  startUpdateCheck(() =>
    useInterfaceStore().pushGlobalNotice({
      messageKey: 'general.frontend_updated',
      level: 'info',
      timeout: 0,
      action: { labelKey: 'general.reload', run: () => location.reload() },
    }),
  )
}

const afterStoreSetup = async ({ pinia, i18n }) => {
  const app = createApp(App)
  // Must have app use pinia before we do anything that touches the store
  // https://pinia.vuejs.org/core-concepts/plugins.html#Introduction
  // "Plugins are only applied to stores created after the plugins themselves, and after pinia is passed to the app, otherwise they won't be applied."
  app.use(pinia)
  // Anything that may show a translated message (desktop notifications
  // triggered by the first fetch, for one) needs this before login starts.
  useI18nStore().setI18n(i18n)

  app.config.errorHandler = (error, instance, info) => {
    console.error(
      'Global Vue Error Handler caught an error:',
      error,
      instance,
      info,
    )
    useInterfaceStore().setGlobalError({ error, instance, info })
  }

  try {
    await loadStores()
  } catch (e) {
    console.error('Cannot load stores:', e)
    useInterfaceStore().pushGlobalNotice({
      messageKey: 'errors.storage_unavailable',
      level: 'error',
    })
  }

  useInterfaceStore().setLayoutWidth(windowWidth())
  useInterfaceStore().setLayoutHeight(windowHeight())

  // the settings stores, for looking into them from the console
  if (process.env.NODE_ENV === 'development') {
    window.syncConfig = useSyncConfigStore()
    window.mergedConfig = useMergedConfigStore()
    window.localConfig = useLocalConfigStore()
    window.highlightConfig = useUserHighlightStore()
  }

  FaviconService.initFaviconService()
  initServiceWorker(useNotificationsStore())

  window.addEventListener('focus', () => updateFocus())

  const staticConfig = await getStaticConfig()
  if (isHosted(staticConfig) && !chosenInstance()) {
    await showInstancePicker({ pinia, i18n })
    return app
  }
  resolveServer(staticConfig)
  await applyConfig(staticConfig)
  await applyTheme()
  await loadInstanceData()
  mountApp(app, i18n)
  return app
}

export default afterStoreSetup
